import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import { useAppSelector } from "../../hooks/hooks";
import { useAppDispatch } from "../../hooks/hooks";
import { createWallet, setUserWalletAddress } from "../../redux/walletSlice";

interface WalletCreatorProps {
  onCreateWallet: (address: string) => void;
  loading?: boolean;
}

const WalletCreator: React.FC<WalletCreatorProps> = ({
  onCreateWallet,
  loading = false,
}) => {
  const [useWalletAddress, setUseWalletAddress] = useState("");
  const [error, setError] = useState("");
  const [isValid, setIsValid] = useState(false);
  const { chainType } = useAppSelector((state) => state.wallet);
  const dispatch = useAppDispatch();

  const validateAddress = (address: string): boolean => {
    setError("");
    setIsValid(false);

    if (!address.trim()) return false;

    const solanaRegex = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
    const evmRegex = /^0x[a-fA-F0-9]{40}$/;

    const valid =
      chainType === "solana"
        ? solanaRegex.test(address)
        : evmRegex.test(address);

    if (!valid) {
      setError(`Invalid ${chainType} address format`);
      return false;
    }

    setIsValid(true);
    return true;
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const address = e.target.value;
    setUseWalletAddress(address);
    validateAddress(address);
  };

  const handleCreateWallet = async () => {
    try {
      const result = await dispatch(createWallet(chainType));
      if (createWallet.fulfilled.match(result)) {
        await dispatch(setUserWalletAddress(result.payload));
        onCreateWallet?.(result.payload);
      }
    } catch (err) {
      setError("Failed to create wallet");
    }
  };

  const handleUseWallet = async () => {
    if (!validateAddress(useWalletAddress)) return;

    try {
      const result = await dispatch(setUserWalletAddress(useWalletAddress));
      if (setUserWalletAddress.fulfilled.match(result)) {
        onCreateWallet?.(useWalletAddress);
      }
    } catch (err) {
      setError("Failed to set wallet address");
    }
  };

  return (
    <Box sx={{ maxWidth: 500, mx: "auto", p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Add Wallet Address
      </Typography>

      <TextField
        fullWidth
        label="Wallet Address"
        variant="outlined"
        value={useWalletAddress}
        onChange={handleAddressChange}
        placeholder="Enter Solana or EVM address"
        sx={{ mb: 2 }}
        error={!!error && useWalletAddress.length > 0}
        helperText={error || (isValid ? "Valid wallet address" : "")}
      />

      {error && useWalletAddress.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        variant="contained"
        color="primary"
        onClick={handleUseWallet}
        disabled={!isValid || loading}
        fullWidth
        sx={{ height: 56 }}
      >
        {loading ? (
          <>
            <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
            Updating...
          </>
        ) : (
          "Use Wallet"
        )}
      </Button>

      <Divider sx={{ my: 3 }} />

      <Button
        variant="contained"
        color="primary"
        onClick={handleCreateWallet}
        fullWidth
        sx={{ height: 56 }}
      >
        {loading ? (
          <>
            <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
            Creating...
          </>
        ) : (
          "Create New Wallet"
        )}
      </Button>
      {chainType === "solana" && (
        <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
          Note: Solana addresses are base58 encoded (32-44 chars).
        </Typography>
      )}
      {chainType === "evm" && (
        <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
          Note: EVM addresses start with 0x followed by 40 hex characters.
        </Typography>
      )}
    </Box>
  );
};

export default WalletCreator;
